import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { datasetRecord as API, form, operators as OperatorAPI } from '../../../api';
import Swal from 'sweetalert2';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    list: {
        width: 300,
        height: 300,
        backgroundColor: theme.palette.background.paper,
        overflow: 'auto',
        border: '1px solid #ccc',
    },
    button: {
        margin: theme.spacing(0.5, 0),
    },
}));

function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
    return [...a, ...not(b, a)];
}

export default function ExportPdfComparisonPage() {
    const classes = useStyles();
    const [operators, setOperators] = useState([]);
    const [selectedOperator, setSelectedOperator] = useState('');

    // Periods (Year/Quarter from Events)
    const [periods, setPeriods] = useState([]); // All available periods
    const [leftPeriods, setLeftPeriods] = useState([]); // Unselected periods
    const [rightPeriods, setRightPeriods] = useState([]); // Selected periods
    const [checked, setChecked] = useState([]);

    // Templates
    const [templates, setTemplates] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);

    // Report Type
    const [reportType, setReportType] = useState('quarter'); // 'quarter' or 'month'

    const [loadingResult, setLoadingResult] = useState(false);

    const [currentTemplateVersion, setCurrentTemplateVersion] = useState(2);
    const [userManuallySelectedVersion, setUserManuallySelectedVersion] = useState(false);

    const leftChecked = intersection(checked, leftPeriods);
    const rightChecked = intersection(checked, rightPeriods);

    useEffect(() => {
        fetchOperators();
        fetchEvents();
        fetchTemplates(2); // Initial fetch
    }, []);

    useEffect(() => {
        if (userManuallySelectedVersion) return; // Skip auto-update if explicitly selected by user

        if (rightPeriods.length > 0) {
            // Find max version among selected periods
            // Default to 1 if undefined
            // If mixed V1 and V2, use V2 (Max)
            const versions = rightPeriods.map(p => p.formVersion || 1);
            const maxVersion = Math.max(...versions);

            if (maxVersion !== currentTemplateVersion) {
                fetchTemplates(maxVersion);
            }
        } else {
            // If no periods selected, revert to default V2 or keep current?
            // Let's revert to V2 as it's the "master" view
            if (currentTemplateVersion !== 2) {
                fetchTemplates(2);
            }
        }
    }, [rightPeriods, userManuallySelectedVersion]);

    const fetchOperators = async () => {
        try {
            const res = await OperatorAPI.getOperators();
            if (res && res.data) {
                setOperators(res.data);
            }
        } catch (error) {
            console.error("Error fetching operators:", error);
        }
    };

    const fetchEvents = async () => {
        try {
            const res = await form.getEvents();
            if (res && res.data) {
                // Format event list for display: "Q{quarter}/{year}"
                // Sort by recent first
                const events = res.data.sort((a, b) => {
                    if (a.year !== b.year) return b.year - a.year;
                    return b.quarter - a.quarter;
                }).map(e => ({
                    id: `${e.year}-${e.quarter}`,
                    label: `${e.year + 543}/${e.quarter}`,
                    year: e.year,
                    quarter: e.quarter,
                    formVersion: e.formVersion
                }));

                setPeriods(events);
                setLeftPeriods(events);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        }
    };

    const fetchTemplates = async (version = 2) => {
        if (currentTemplateVersion !== version) setCurrentTemplateVersion(version);
        try {
            setTemplates([]); // Clear during load to indicate change? Or keep old?
            // Better to keep old until new arrives to avoid flickering, but clearing shows update.

            const res = await API.getHeadsForExcelExport(version);
            if (res && Array.isArray(res)) {
                // Deduplicate by 'no' logic same as original page
                const unique = [];
                const seen = new Set();
                for (const t of res) {
                    if (t.no && (t.no.startsWith('1.') || t.no.startsWith('2.')) && !seen.has(t.no)) {
                        seen.add(t.no);
                        unique.push(t);
                    }
                }
                // Sort
                const sorted = unique.sort((a, b) => {
                    const partsA = (a.no || '').toString().split('.').map(x => parseInt(x, 10));
                    const partsB = (b.no || '').toString().split('.').map(x => parseInt(x, 10));

                    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
                        const valA = isNaN(partsA[i]) ? 0 : partsA[i];
                        const valB = isNaN(partsB[i]) ? 0 : partsB[i];
                        if (valA !== valB) return valA - valB;
                    }
                    return 0;
                });
                setTemplates(sorted);
                // Clear selection if templates change? 
                // Currently IDs (Nos) might match across versions? 
                // If Nos are same (e.g. 1.1.1), we can keep selection. 
                // If ID disappears, maybe filter selectedTemplates?
                // Let's clear to be safe or filter.
                setSelectedTemplates((prev) => prev.filter(p => sorted.some(s => s.no === p)));
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    // Transfer List Handlers
    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        setRightPeriods(rightPeriods.concat(leftChecked));
        setLeftPeriods(not(leftPeriods, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeftPeriods(leftPeriods.concat(rightChecked));
        setRightPeriods(not(rightPeriods, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    // Template Handlers
    const handleTemplateToggle = (value) => () => {
        const currentIndex = selectedTemplates.indexOf(value);
        const newChecked = [...selectedTemplates];
        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setSelectedTemplates(newChecked);
    };

    const handleSelectAllTemplates = () => {
        if (selectedTemplates.length === templates.length) {
            setSelectedTemplates([]);
        } else {
            setSelectedTemplates(templates.map(t => t.no));
        }
    };

    const handleExport = async (type = 'pdf') => {
        if (!selectedOperator) {
            Swal.fire('Error', 'กรุณาเลือกผู้ประกอบการ', 'warning');
            return;
        }
        if (rightPeriods.length === 0) {
            Swal.fire('Error', 'กรุณาเลือกช่วงเวลาเปรียบเทียบ', 'warning');
            return;
        }
        if (selectedTemplates.length === 0) {
            Swal.fire('Error', 'กรุณาเลือกหัวข้อรายงาน', 'warning');
            return;
        }

        setLoadingResult(true);
        Swal.fire({
            title: 'กำลังเตรียมข้อมูล...',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            // 1. Fetch Heads (Reuse existing logic to get Head IDs)
            // Ideally backend should handle this resolving but we do it here as per current flow
            const headPromises = rightPeriods.map(period =>
                API.getCurrentHeadAdmin(selectedOperator, period.year, period.quarter)
                    .then(res => res.data && res.data.id ? res.data : null)
                    .catch(e => null)
            );

            const heads = await Promise.all(headPromises);
            const validHeads = heads.filter(h => h !== null);

            if (validHeads.length === 0) {
                setLoadingResult(false);
                Swal.fire('Error', 'ไม่พบข้อมูลรายงานสำหรับช่วงเวลาที่เลือก', 'error');
                return;
            }

            // 2. Call Export API
            Swal.update({ title: `กำลังออกรายงาน ${type.toUpperCase()}...`, });

            const payload = {
                HeadIds: validHeads.map(h => h.id),
                TemplateNos: selectedTemplates,
                ReportType: reportType
            };

            let blob;
            let extension = 'pdf';
            if (type === 'excel') {
                blob = await API.exportComparisonOneOperatorExcel(payload);
                extension = 'xlsx';
            } else {
                blob = await API.exportComparisonOneOperator(payload);
            }

            if (blob && blob.size > 0) {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                const timestamp = new Date().toISOString().replace(/[-T:\.Z]/g, '').slice(0, 14);
                link.setAttribute('download', `รายงานสรุปผลค่าชี้วัดคุณภาพบริการรายบริษัท.${extension}`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                Swal.close();
            } else {
                Swal.fire('Error', 'ไม่สามารถออกรายงานได้ (Empty File)', 'error');
            }
        } catch (error) {
            console.error("Export Error:", error);
            Swal.fire('Error', 'เกิดข้อผิดพลาดในการออกรายงาน', 'error');
        } finally {
            setLoadingResult(false);
        }
    };

    const customList = (title, items) => (
        <Card>
            <CardHeader
                avatar={
                    <Checkbox
                        onClick={handleToggleAll(items)}
                        checked={numberOfChecked(items) === items.length && items.length !== 0}
                        indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
                        disabled={items.length === 0}
                        inputProps={{ 'aria-label': 'all items selected' }}
                    />
                }
                title={title}
                subheader={`${numberOfChecked(items)}/${items.length} selected`}
            />
            <Divider />
            <List className={classes.list} dense component="div" role="list">
                {items.map((value) => {
                    const labelId = `transfer-list-all-item-${value.id}-label`;
                    return (
                        <ListItem key={value.id} role="listitem" button onClick={handleToggle(value)}>
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.indexOf(value) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={value.label} />
                        </ListItem>
                    );
                })}
                <ListItem />
            </List>
        </Card>
    );

    return (
        <div className={classes.root}>
            <Typography variant="h4" gutterBottom>
                ออกรายงานสรุปผลค่าชี้วัดคุณภาพบริการรายบริษัท
            </Typography>

            <Paper className={classes.paper}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="ผู้ประกอบการ"
                            value={selectedOperator}
                            onChange={(e) => setSelectedOperator(e.target.value)}
                            fullWidth
                            variant="outlined"
                        >
                            {operators.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.operatorName} ({option.taxNo})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="รูปแบบรายงาน"
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            fullWidth
                            variant="outlined"
                        >
                            <MenuItem value="quarter">เปรียบเทียบร้อยละ (Quarter/Quarter)</MenuItem>
                            <MenuItem value="month">รายเดือน (Month/Month)</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2} justify="center" alignItems="center" className={classes.root}>
                <Grid item>{customList('ช่วงเวลาที่เลือกได้', leftPeriods)}</Grid>
                <Grid item>
                    <Grid container direction="column" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.button}
                            onClick={handleCheckedRight}
                            disabled={leftChecked.length === 0}
                            aria-label="move selected right"
                        >
                            &gt;
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.button}
                            onClick={handleCheckedLeft}
                            disabled={rightChecked.length === 0}
                            aria-label="move selected left"
                        >
                            &lt;
                        </Button>
                    </Grid>
                </Grid>
                <Grid item>{customList('ช่วงเวลาที่เปรียบเทียบ', rightPeriods)}</Grid>
            </Grid>

            <Paper className={classes.paper} style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" gutterBottom style={{ marginBottom: 0, marginRight: 16 }}>
                            เลือกค่าชี้วัดคุณภาพบริการ
                        </Typography>
                        <TextField
                            select
                            label="เวอร์ชั่น"
                            value={currentTemplateVersion}
                            onChange={(e) => {
                                setUserManuallySelectedVersion(true);
                                fetchTemplates(e.target.value);
                            }}
                            variant="outlined"
                            size="small"
                        >
                            <MenuItem value={1}>แบบฟอร์มประกาศปีก่อนหน้า</MenuItem>
                            <MenuItem value={2}>แบบฟอร์มประกาศปี 2568</MenuItem>
                        </TextField>
                    </div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={templates.length > 0 && selectedTemplates.length === templates.length}
                                indeterminate={selectedTemplates.length > 0 && selectedTemplates.length < templates.length}
                                onChange={handleSelectAllTemplates}
                                color="primary"
                            />
                        }
                        label="เลือกทั้งหมด"
                    />
                </div>
                <List style={{ maxHeight: 300, overflow: 'auto' }}>
                    {templates.map((template) => (
                        <ListItem key={template.no} button onClick={handleTemplateToggle(template.no)}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={selectedTemplates.indexOf(template.no) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText primary={`${template.no} ${template.label}`} />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {loadingResult && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                    <CircularProgress />
                    <Typography variant="body1" style={{ marginLeft: 10 }}>กำลังประมวลผล...</Typography>
                </div>
            )}

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleExport('pdf')}
                        size="large"
                        fullWidth
                        disabled={loadingResult}
                    >
                        ดาวน์โหลดรายงานเปรียบเทียบ (PDF)
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleExport('excel')}
                        size="large"
                        fullWidth
                        style={{ marginLeft: 0, background: '#107c41' }}
                        disabled={loadingResult}
                        startIcon={<i className="fas fa-file-excel" />}
                    >
                        ดาวน์โหลดรายงาน (Excel)
                    </Button>
                </Grid>
            </Grid>

        </div>
    );
}
